import React from "react";
import { AlertTriangle, RefreshCw, Home, Send, CheckCircle2 } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * App-wide error boundary. Catches uncaught React render/effect errors and
 * shows a friendly Spanish fallback with options to:
 *  - Recargar la página (window.location.reload)
 *  - Ir al inicio (clears any broken route state)
 *  - Reportar el error al backend (/api/errors/report)
 *
 * Receives `children` and renders them when no error has occurred.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            error: null,
            info: null,
            reportState: "idle", // 'idle' | 'sending' | 'sent' | 'failed'
        };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        this.setState({ info });
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary] Uncaught render error:", error, info);
        // Auto-fire a best-effort silent report so we have visibility even if
        // the user never clicks "Reportar".
        this._sendReport(error, info, /* silent */ true);
    }

    _sendReport = async (error, info, silent = false) => {
        if (!silent) this.setState({ reportState: "sending" });
        try {
            await fetch(`${REACT_APP_BACKEND_URL}/api/errors/report`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    message: (error && (error.message || String(error))) || "Unknown error",
                    stack: (error && error.stack) || "",
                    component_stack: (info && info.componentStack) || "",
                    url: window.location.href,
                    user_agent: navigator.userAgent,
                }),
            });
            if (!silent) this.setState({ reportState: "sent" });
        } catch (_e) {
            if (!silent) this.setState({ reportState: "failed" });
        }
    };

    _reportClick = () => {
        const { error, info } = this.state;
        this._sendReport(error, info, false);
    };

    _reset = () => {
        this.setState({ error: null, info: null, reportState: "idle" });
    };

    render() {
        const { error, reportState } = this.state;
        if (!error) return this.props.children;

        const message = error.message || String(error);
        return (
            <div
                data-testid="error-boundary"
                className="notranslate"
                translate="no"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#0B0B0F",
                    color: "#E5E5EA",
                    padding: 24,
                    fontFamily: "Inter, system-ui, sans-serif",
                }}
            >
                <div style={{ maxWidth: 560, width: "100%", background: "#14141A", border: "1px solid #2A2A33", borderRadius: 16, padding: 32 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(220,38,38,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <AlertTriangle size={22} color="#F87171" />
                        </div>
                        <div>
                            <h1 style={{ fontSize: 20, margin: 0, fontWeight: 700 }}>Algo salió mal</h1>
                            <p style={{ margin: "2px 0 0", fontSize: 13, color: "#A3A3A8" }}>
                                La aplicación encontró un error inesperado. No te preocupes, tus datos están a salvo.
                            </p>
                        </div>
                    </div>

                    <details style={{ background: "#0F0F14", border: "1px solid #26262B", borderRadius: 8, padding: 12, marginBottom: 18 }}>
                        <summary style={{ cursor: "pointer", fontSize: 12, color: "#A3A3A8", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em" }}>
                            Detalles técnicos
                        </summary>
                        <pre style={{ marginTop: 10, fontSize: 11, color: "#F87171", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 220, overflow: "auto", margin: 0 }}>
                            {message}
                        </pre>
                    </details>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            data-testid="error-boundary-reload"
                            style={btnPrimary}
                        >
                            <RefreshCw size={14} /> Recargar página
                        </button>
                        <button
                            type="button"
                            onClick={() => { window.location.href = "/"; }}
                            data-testid="error-boundary-home"
                            style={btnSecondary}
                        >
                            <Home size={14} /> Ir al inicio
                        </button>
                        <button
                            type="button"
                            onClick={this._reportClick}
                            disabled={reportState === "sending" || reportState === "sent"}
                            data-testid="error-boundary-report"
                            style={reportState === "sent" ? btnSuccess : btnSecondary}
                        >
                            {reportState === "sending" && <><span className="loader" /> Enviando…</>}
                            {reportState === "sent" && <><CheckCircle2 size={14} /> Reporte enviado</>}
                            {reportState === "failed" && <><Send size={14} /> Reintentar reporte</>}
                            {reportState === "idle" && <><Send size={14} /> Reportar al equipo</>}
                        </button>
                    </div>

                    <p style={{ marginTop: 18, fontSize: 11, color: "#71717A" }}>
                        Si esto ocurre con frecuencia desde Google Chrome, asegúrate de que <b>Google Translate</b> no esté traduciendo automáticamente esta página.
                    </p>
                </div>
            </div>
        );
    }
}

const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    border: "1px solid transparent",
    cursor: "pointer",
    transition: "all 120ms ease",
};
const btnPrimary = { ...btnBase, background: "#3B82F6", color: "#fff" };
const btnSecondary = { ...btnBase, background: "#1F1F26", color: "#E5E5EA", borderColor: "#2A2A33" };
const btnSuccess = { ...btnBase, background: "rgba(22,163,74,.18)", color: "#86EFAC", borderColor: "rgba(22,163,74,.4)" };
